import React from 'react';
import { Row, List, Col, Button, Divider } from 'antd';
import styled from 'styled-components';
import Link from 'next/link';
import NImage from 'next/image';
import EventCard from 'components/EventCard';
import SocialLinks from 'components/SocialLinks';
import RelativeContentTabs from 'components/RelativeContentTabs';
import EventDate from 'components/EventDate';
import Section, { SectionTitle } from 'components/Section';
import { SkyscraperMobile } from 'components/Ads';
import Image from 'components/Image';
import DefaultBG from 'public/static/images/wydarzenia-konferencje.png';
import PatronatImage from 'public/static/images/patronat.svg';
import dynamic from 'next/dynamic';
import { merriweather } from 'styles/GlobalStyle';

const DynamicForm = dynamic(() => import('components/DynamicForm'), {
    ssr: false
});

const BranchSection = dynamic(() => import('components/BranchSection'), {
    ssr: true
});

const LogoContainer = styled.div`
    position: absolute;
    height: 270px;
    width: 270px;
    top: 50%;
    left: 3%;
    border: 1px solid #b7cbd9;
    @media (max-width: 992px) {
        height: 200px;
        width: 200px;
        top: 55%;
        left: 50%;
        transform: translateX(-50%);
    }
    @media (max-width: 576px) {
        top: 15%;
    }
`;

const EventName = styled.h1`
    font-size: 32px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    @media (max-width: 992px) {
        text-align: center;
    }
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const EventType = styled.h2`
    font-size: 22px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    text-transform: capitalize;
    color: ${({ theme }) => theme.colors.brownishGrey};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const EventDateText = styled.p`
    font-size: 22px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.brownishGrey};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const EventText = styled.span`
    font-size: 20px;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.brownishGrey};
`;

const Address = styled.h2`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.primary};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const URL = styled.p`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.4;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    a {
        color: ${({ theme }) => theme.colors.brownishGrey};
    }
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const AboutTitle = styled.p`
    font-family: ${merriweather.style.fontFamily};
    text-transform: uppercase;
    font-size: 18px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 2.22;
    letter-spacing: 0.36px;
    text-align: left;
    color: ${({ theme }) => theme.colors.primary};
    @media (max-width: 992px) {
        text-align: center;
    }
`;

const AboutText = styled.div`
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.56;
    letter-spacing: normal;
    text-align: left;
    margin: 0;
    color: ${({ theme }) => theme.colors.brownishGrey};
    > p {
        margin: 0;
    }
`;

const OrganizingCompanySection = styled(Section)`
    @media (max-width: 992px) {
        align-items: center;
        margin-top: 75px;
    }
`;

const EventsSubTitle = styled.span`
    font-size: 20px;
    font-weight: 300;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.3;
    letter-spacing: normal;
    text-align: left;
    color: ${(props) => props.color || props.theme.colors.greyishBrown};
`;

export interface Props {
    event: Occurrence;
    events: Occurrence[];
    organizedEvents: Occurrence[];
    ads: PageAds;
}

const EventDetails = ({ event, organizedEvents, ads }: Props): React.JSX.Element => {
    const {
        name,
        type,
        logo,
        location,
        seoSlug,
        startDate,
        endDate,
        branches,
        socialLinks,
        premium,
        premiumBackground,
        premiumContent,
        contact,
        description,
        articles,
        galleries,
        videos,
        website,
        whitePapers,
        organizingCompany,
        organizer,
        sideForm
    } = event;
    const { skyscraper } = ads;

    return (
        <>
            <Row style={{ position: 'relative' }}>
                <Col span={24}>
                    {premium && premiumBackground && (
                        <Image
                            type="billboard"
                            width={1200}
                            height={303}
                            src={premiumBackground}
                            alt={name}
                            style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                        />
                    )}

                    {(!premium || !premiumBackground) && (
                        <NImage
                            width={1200}
                            height={303}
                            src={DefaultBG}
                            alt={name}
                            style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                        />
                    )}

                    <LogoContainer>
                        <Image
                            type="logo"
                            width={270}
                            height={270}
                            src={logo}
                            alt={name}
                            style={{ maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                        />
                    </LogoContainer>
                </Col>
            </Row>
            <Section gutter={16}>
                <Col
                    xs={{ span: 24, order: 2 }}
                    lg={{ offset: 8, span: 11, order: 1 }}
                    xxl={{ offset: 7, span: 11, order: 1 }}
                >
                    <Row>
                        <Col span={24}>
                            <EventName>{name}</EventName>
                            <EventType>{type}</EventType>
                            <EventDateText>
                                <EventDate startDate={startDate} endDate={endDate} />
                            </EventDateText>
                            <Address>{location}</Address>
                            {website && (
                                <URL>
                                    <a target="_blank" href={website} rel="noopener noreferrer">
                                        {website}
                                    </a>
                                </URL>
                            )}
                        </Col>
                        <Col span={24} style={{ marginTop: 25 }}>
                            <EventText>
                                Organizator: <strong>{organizer}</strong>
                            </EventText>
                        </Col>
                        {contact && (
                            <Col span={24} style={{ marginTop: 25 }}>
                                <EventText>Kontakt:</EventText>
                                <AboutText dangerouslySetInnerHTML={{ __html: contact }} />
                            </Col>
                        )}
                        <Col span={24}>
                            <Divider
                                style={{
                                    margin: '5px 0 24px 0',
                                    width: '80%',
                                    minWidth: '80%'
                                }}
                            />
                            <Link href={`/wydarzenia/${seoSlug}/kontakt`}>
                                <Button style={{ padding: '0 30px' }}>Kontakt</Button>
                            </Link>
                        </Col>
                    </Row>
                </Col>
                <Col
                    xs={{ span: 24, order: 1 }}
                    lg={{ span: 5, order: 2 }}
                    xxl={{ span: 6, order: 2 }}
                >
                    <OrganizingCompanySection gutter={[16, 16]} justify="space-between">
                        <Col
                            xs={{ span: 8, order: 1 }}
                            md={{ span: 4, order: 1 }}
                            lg={{ span: 24, order: 1 }}
                        >
                            {organizingCompany && (
                                <div>
                                    <Link href={`/katalog-firm/${organizingCompany.seoSlug}`}>
                                        <Image
                                            type="logo"
                                            alt={organizingCompany.name}
                                            height={272}
                                            width={272}
                                            src={organizingCompany.logo}
                                            style={{
                                                maxWidth: '100%',
                                                height: 'auto',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Link>
                                </div>
                            )}
                        </Col>
                        <Col
                            xs={{ span: 16, order: 2 }}
                            md={{ span: 20, order: 2 }}
                            lg={{ span: 24, order: 2 }}
                        >
                            <Row justify="end">
                                {event.patronate && (
                                    <Col>
                                        <NImage
                                            src={PatronatImage}
                                            width={120}
                                            height={22}
                                            alt="patronat"
                                            style={{ maxWidth: '100%', objectFit: 'contain' }}
                                        />
                                    </Col>
                                )}
                                <Col>
                                    <SocialLinks {...socialLinks} />
                                </Col>
                            </Row>
                        </Col>
                    </OrganizingCompanySection>
                </Col>
            </Section>
            <Divider />
            <Row justify="center">
                <Col xs={24} sm={12}>
                    <SkyscraperMobile id={skyscraper[0]} margin="12px auto 12px auto" />
                </Col>
                <Col xs={24} sm={12}>
                    <SkyscraperMobile id={skyscraper[1]} margin="12px auto 12px auto" />
                </Col>
            </Row>
            <Section gutter={16}>
                <Col xs={24} md={18}>
                    <Row>
                        <Col xs={24} md={6}>
                            <AboutTitle>O WYDARZENIU</AboutTitle>
                        </Col>
                        <Col xs={24} md={18}>
                            <Row>
                                <Col span={24}>
                                    <AboutText dangerouslySetInnerHTML={{ __html: description }} />
                                </Col>
                            </Row>
                            {premium && (
                                <Row>
                                    <Col span={24}>
                                        <AboutText
                                            dangerouslySetInnerHTML={{ __html: premiumContent }}
                                        />
                                    </Col>
                                </Row>
                            )}
                        </Col>
                        <Divider />
                        {premium && (
                            <Col span={24} style={{ padding: '25px 0 0 0' }}>
                                <AboutTitle>Informacje POWIĄZANE Z WYDARZENIEM</AboutTitle>
                                <RelativeContentTabs
                                    articles={articles}
                                    galleries={galleries}
                                    videos={videos}
                                    whitePapers={whitePapers}
                                />
                            </Col>
                        )}
                    </Row>
                </Col>

                <Col xs={24} md={6}>
                    {branches.length > 0 && (
                        <>
                            <BranchSection branches={branches} />
                            <Divider />
                        </>
                    )}
                    {sideForm && (
                        <>
                            <Section>
                                <Col span={24}>
                                    <DynamicForm form={sideForm} />
                                </Col>
                            </Section>
                            <Divider />
                        </>
                    )}
                    <Row gutter={[16, 32]}>
                        {organizedEvents?.length > 0 && (
                            <Col span={24} style={{ paddingTop: 20 }}>
                                <SectionTitle
                                    title="Najbliższe wydarzenia"
                                    style={{ marginBottom: 1 }}
                                />

                                <List
                                    itemLayout="vertical"
                                    dataSource={organizedEvents}
                                    rowKey={({ seoSlug }: Occurrence): string => seoSlug}
                                    header={<EventsSubTitle>tego organizatora</EventsSubTitle>}
                                    renderItem={(item: Occurrence) => (
                                        <List.Item>
                                            <EventCard
                                                logo={item.logo}
                                                logoThumb={item.logo}
                                                title={item.name}
                                                type={item.type}
                                                slug={item.seoSlug}
                                                startDate={item.startDate}
                                                endDate={item.endDate}
                                            />
                                        </List.Item>
                                    )}
                                />
                            </Col>
                        )}
                    </Row>
                </Col>
            </Section>
        </>
    );
};

export default EventDetails;
